import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MdGroups, MdMedicalServices, MdMonitorHeart, MdWorkspacePremium,
  MdEventAvailable, MdCalendarToday,
  MdCheckCircle, MdCancel, MdPayments,
  MdBadge, MdAssignment,
} from 'react-icons/md';
import { PageShell, StatCard, DataTable, Badge } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { STAFF_ROLES } from '../../config/plans';
import { ROLES } from '../../permissions';

const TODAY = new Date().toISOString().slice(0, 10);

const SHIFT_TYPES = ['Morning (7am–2pm)', 'Afternoon (2pm–9pm)', 'Night (9pm–7am)', 'General (9am–5pm)'];
const LEAVE_TYPES = ['Casual Leave', 'Sick Leave', 'Earned Leave', 'Maternity/Paternity', 'Unpaid Leave', 'Emergency'];
const LEAVE_STATUS_COLORS = {
  Pending: { bg: '#fefce8', color: '#ca8a04', border: '#fde68a' },
  Approved: { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  Rejected: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
};

function StatusChip({ status }) {
  const c = LEAVE_STATUS_COLORS[status] || LEAVE_STATUS_COLORS.Pending;
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: 999, padding: '2px 10px', fontSize: 11.5, fontWeight: 700,
    }}>
      {status}
    </span>
  );
}

/* ── Attendance grid ── */
function AttendanceTab({ staff }) {
  const [date, setDate] = useState(TODAY);
  const [attendance, setAttendance] = useState({});
  const [shift, setShift] = useState({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [saveErr, setSaveErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadErr('');
      try {
        const data = await ipApi.getHrAttendance(date);
        const map = {};
        (data.records || []).forEach((r) => { map[r.staffId] = r.status; });
        (staff || []).forEach((s) => {
          const id = s.uid || s.staffId || s.name;
          if (!map[id]) map[id] = 'Present';
        });
        if (!cancelled) setAttendance(map);
      } catch (e) {
        if (!cancelled) {
          setLoadErr(e.message || 'Could not load attendance from server');
          setAttendance({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date, staff]);

  const mark = (id, status) => {
    setAttendance((a) => ({ ...a, [id]: status }));
    setSaved(false);
  };

  const presentCount = Object.values(attendance).filter((s) => s === 'Present').length;
  const absentCount = Object.values(attendance).filter((s) => s === 'Absent').length;
  const leaveCount = Object.values(attendance).filter((s) => s === 'On Leave').length;

  const saveAttendance = async () => {
    setSaveErr('');
    try {
      const entries = (staff || []).map((s) => ({
        staffId: s.uid || s.staffId,
        staffName: s.name,
        status: attendance[s.uid || s.staffId || s.name] || 'Present',
        shift: shift[s.uid || s.staffId || s.name] || '',
      }));
      await ipApi.saveHrAttendance({ date, entries });
      setSaved(true);
    } catch (e) {
      setSaveErr(e.message);
    }
  };

  return (
    <div>
      {/* Date selector + summary */}
      <div className="att-toolbar">
        <div className="row-actions">
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Date:</label>
          <input type="date" className="input" style={{ width: 160 }} value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="att-summary">
          <span className="att-sum-chip" style={{ background: '#f0fdf4', color: '#16a34a' }}>✓ Present: {presentCount}</span>
          <span className="att-sum-chip" style={{ background: '#fef2f2', color: '#dc2626' }}>✗ Absent: {absentCount}</span>
          <span className="att-sum-chip" style={{ background: '#fefce8', color: '#ca8a04' }}>⏸ On Leave: {leaveCount}</span>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={loading}
          onClick={saveAttendance}
        >
          {saved ? 'Saved' : loading ? 'Loading…' : 'Save Attendance'}
        </button>
      </div>
      {saveErr && <div className="error mb">{saveErr}</div>}
      {loadErr && <div className="error mb">{loadErr}</div>}

      {saved && <div className="info-banner mb">Attendance for {date} saved successfully.</div>}

      <div className="att-grid-header">
        <span>Name</span>
        <span>Role</span>
        <span>Shift</span>
        <span>Mark Attendance</span>
      </div>
      {(staff || []).map((s) => {
        const id = s.uid || s.staffId || s.name;
        const att = attendance[id] || 'Present';
        return (
          <div key={id} className="att-row">
            <div className="att-name">
              <span className="att-avatar">{(s.name || 'S').charAt(0)}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.staffId || '—'}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#475569' }}>{ROLES[s.role]?.label || s.role || '—'}</div>
            <select
              className="input input-sm"
              style={{ width: 180 }}
              value={shift[id] || ''}
              onChange={(e) => setShift((sh) => ({ ...sh, [id]: e.target.value }))}
            >
              <option value="">Select shift</option>
              {SHIFT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <div className="att-btn-group">
              {['Present', 'Absent', 'On Leave', 'Half Day'].map((status) => (
                <button
                  key={status}
                  type="button"
                  className="att-status-btn"
                  style={{
                    background: att === status ? (
                      status === 'Present' ? '#16a34a' :
                      status === 'Absent' ? '#dc2626' :
                      status === 'On Leave' ? '#ca8a04' : '#2563eb'
                    ) : '#f1f5f9',
                    color: att === status ? '#fff' : '#475569',
                    border: `1.5px solid ${att === status ? 'transparent' : '#e2e8f0'}`,
                  }}
                  onClick={() => mark(id, status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {(!staff || !staff.length) && (
        <p className="hint table-empty">No staff found. <Link to="/manager/admin">Add staff logins →</Link></p>
      )}
    </div>
  );
}

/* ── Leave management ── */
function LeaveTab({ staff }) {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', type: 'Casual Leave', from: TODAY, to: TODAY, reason: '' });
  const [err, setErr] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const data = await ipApi.getHrLeave(80);
      setLeaves(data.leaves || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const picked = (staff || []).find((s) => s.name === form.name);
      await ipApi.createHrLeave({
        staffId: picked?.uid || picked?.staffId || '',
        staffName: form.name,
        leaveType: form.type,
        fromDate: form.from,
        toDate: form.to,
        reason: form.reason,
      });
      setForm({ name: '', role: '', type: 'Casual Leave', from: TODAY, to: TODAY, reason: '' });
      setShowForm(false);
      await reload();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await ipApi.patchHrLeave(id, { status });
      await reload();
    } catch (ex) {
      setErr(ex.message);
    }
  };

  return (
    <div>
      {err && <div className="error mb">{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : '+ Apply Leave'}
        </button>
      </div>

      {showForm && (
        <form className="form-grid card mb" onSubmit={add}>
          <h3 style={{ gridColumn: '1/-1', marginTop: 0 }}>Leave Application</h3>
          <div className="field">
            <label>Staff name *</label>
            <select className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}>
              <option value="">Select staff</option>
              {(staff || []).map((s) => <option key={s.uid || s.name} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Leave type</label>
            <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {LEAVE_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field">
            <label>From date</label>
            <input type="date" className="input" required value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
          </div>
          <div className="field">
            <label>To date</label>
            <input type="date" className="input" required value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
          </div>
          <div className="field full">
            <label>Reason</label>
            <input className="input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Brief reason for leave" />
          </div>
          <div className="form-actions full">
            <button type="submit" className="btn btn-primary">Submit Application</button>
          </div>
        </form>
      )}

      <div className="leave-list">
        {loading && <p className="hint">Loading leave records…</p>}
        {leaves.map((l) => (
          <div key={l.leaveId || l.id} className="leave-card">
            <div className="leave-card-head">
              <div>
                <div className="leave-card-name">{l.staffName || l.name}</div>
                <div className="leave-card-meta">{l.leaveType || l.type} · {l.fromDate || l.from} to {l.toDate || l.to}</div>
                {l.reason && <div className="leave-card-reason">"{l.reason}"</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <StatusChip status={l.status} />
                <div className="leave-days">
                  {Math.max(1, Math.ceil((new Date(l.toDate || l.to) - new Date(l.fromDate || l.from)) / 86400000) + 1)} day(s)
                </div>
              </div>
            </div>
            {l.status === 'Pending' && (
              <div className="leave-actions">
                <button type="button" className="btn btn-sm" style={{ background: '#16a34a', color: '#fff' }} onClick={() => updateStatus(l.leaveId || l.id, 'Approved')}>
                  <MdCheckCircle /> Approve
                </button>
                <button type="button" className="btn btn-sm" style={{ background: '#dc2626', color: '#fff' }} onClick={() => updateStatus(l.leaveId || l.id, 'Rejected')}>
                  <MdCancel /> Reject
                </button>
              </div>
            )}
          </div>
        ))}
        {leaves.length === 0 && <p className="hint table-empty">No leave applications.</p>}
      </div>
    </div>
  );
}

/* ── Duty Roster (API-backed) ── */
function RosterTab({ staff }) {
  const month = new Date().toISOString().slice(0, 7);
  const dept = 'Hospital';
  const { data, reload } = useLiveData(() => ipApi.getRoster(month, dept), [month]);
  const [weekStart, setWeekStart] = useState(TODAY);
  const [roster, setRoster] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const saved = data?.roster?.shifts || {};
    setRoster(saved);
  }, [data]);

  const weekDays = useMemo(() => {
    const days = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d.toISOString().slice(0, 10));
    }
    return days;
  }, [weekStart]);

  const setRosterEntry = (staffId, day, val) => {
    setRoster((r) => ({ ...r, [`${staffId}_${day}`]: val }));
  };

  const saveRoster = async () => {
    setSaving(true);
    setMsg('');
    try {
      await ipApi.saveRoster({ month, dept, shifts: roster });
      setMsg('Roster saved.');
      await reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="row-actions mb">
        <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Week starting:</label>
        <input type="date" className="input" style={{ width: 160 }} value={weekStart} onChange={(e) => setWeekStart(e.target.value)} />
        <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={saveRoster}>{saving ? 'Saving…' : 'Save roster'}</button>
        <Link to="/hms/roster" className="btn btn-secondary btn-sm">Full monthly roster →</Link>
      </div>
      {msg && <div className="hint mb" style={{ color: msg.includes('saved') ? '#16a34a' : '#dc2626' }}>{msg}</div>}

      <div className="roster-table-wrap">
        <table className="roster-table">
          <thead>
            <tr>
              <th>Staff</th>
              {weekDays.map((d) => (
                <th key={d}>
                  <div>{new Date(d).toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                  <div style={{ fontWeight: 400, fontSize: 11 }}>{d.slice(5)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(staff || []).slice(0, 12).map((s) => {
              const id = s.uid || s.staffId || s.name;
              return (
                <tr key={id}>
                  <td>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{s.name}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{ROLES[s.role]?.label || s.role}</div>
                  </td>
                  {weekDays.map((d) => (
                    <td key={d}>
                      <select
                        className="roster-cell-select"
                        value={roster[`${id}_${d}`] || ''}
                        onChange={(e) => setRosterEntry(id, d, e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="M">Morning</option>
                        <option value="A">Afternoon</option>
                        <option value="N">Night</option>
                        <option value="OFF">Off</option>
                        <option value="L">Leave</option>
                      </select>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {(!staff || !staff.length) && (
        <p className="hint table-empty">No staff to roster. <Link to="/manager/admin">Add staff →</Link></p>
      )}
    </div>
  );
}

export default function HrDashboard() {
  const { user } = useAuth();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getStaffDirectory(), []);
  const [tab, setTab] = useState('overview');

  const staff = useMemo(() => data?.staff || [], [data]);
  const byRole = useMemo(() => {
    const m = {};
    STAFF_ROLES.forEach((r) => { m[r.id] = staff.filter((s) => s.role === r.id); });
    return m;
  }, [staff]);

  const staffCols = [
    { key: 'name', label: 'Name', render: (r) => (
      <div className="cell-stack">
        <span className="cell-strong">{r.name}</span>
        <span className="cell-sub">{r.staffId || '—'}</span>
      </div>
    )},
    { key: 'role', label: 'Role', render: (r) => <Badge tone="info">{ROLES[r.role]?.label || r.role}</Badge> },
    { key: 'specialty', label: 'Specialty / Dept', render: (r) => r.specialty || r.department || '—' },
    { key: 'phone', label: 'Phone', render: (r) => r.phone || '—' },
    { key: 'email', label: 'Email', render: (r) => r.email || '—' },
  ];

  return (
    <PageShell
      title="HR Dashboard"
      subtitle="Staff directory · Attendance · Leave · Duty roster"
      actions={
        <>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => reload()}>Refresh</button>
          {user?.role === 'manager' && <Link to="/manager/admin" className="btn btn-primary">+ Add staff</Link>}
        </>
      }
    >
      {error && <div className="error mb">{error}</div>}

      {/* KPI cards */}
      <div className="stat-grid">
        <StatCard label="Total Staff" value={staff.length} accent="#6366f1" icon={MdGroups} loading={loading} />
        <StatCard label="Doctors" value={byRole.doctor?.length || 0} accent="#0d6e6a" icon={MdMedicalServices} loading={loading} />
        <StatCard label="Nurses" value={byRole.nurse?.length || 0} accent="#ec4899" icon={MdMonitorHeart} loading={loading} />
        <StatCard label="Plan" value={data?.hospital?.plan || '—'} accent="#8b5cf6" icon={MdWorkspacePremium} loading={loading} />
      </div>

      {/* Role breakdown */}
      <div className="card mb">
        <div className="card-head-row">
          <h3>Staff by Role</h3>
          {user?.role === 'manager' && <Link to="/manager/admin" className="btn btn-sm btn-primary">Manage logins</Link>}
        </div>
        <div className="monitor-count-grid">
          {STAFF_ROLES.map((r) => (
            <div key={r.id} className="monitor-count-cell">
              <span className="monitor-count-val">{byRole[r.id]?.length || 0}</span>
              <span className="monitor-count-label">{r.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[
          { id: 'overview', label: 'Directory', icon: MdBadge },
          { id: 'attendance', label: 'Attendance', icon: MdCalendarToday },
          { id: 'leaves', label: 'Leave Mgmt', icon: MdEventAvailable },
          { id: 'roster', label: 'Duty Roster', icon: MdAssignment },
          { id: 'payroll', label: 'Payroll', icon: MdPayments },
          { id: 'performance', label: 'Performance', icon: MdWorkspacePremium },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>
            <Icon style={{ verticalAlign: '-2px', marginRight: 4 }} /> {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card">
          <h3>Staff Directory</h3>
          <p className="hint mb">All active staff members at this hospital.</p>
          <DataTable columns={staffCols} rows={staff} keyField="uid" loading={loading} emptyMessage="No staff added yet." />
        </div>
      )}

      {tab === 'attendance' && (
        <div className="card">
          <h3>Daily Attendance Register</h3>
          <p className="hint mb">Mark attendance for each staff member.</p>
          <AttendanceTab staff={staff} />
        </div>
      )}

      {tab === 'leaves' && (
        <div className="card">
          <h3>Leave Management</h3>
          <p className="hint mb">Submit and approve leave applications.</p>
          <LeaveTab staff={staff} />
        </div>
      )}

      {tab === 'roster' && (
        <div className="card">
          <h3>Weekly Duty Roster</h3>
          <p className="hint mb">Assign shifts for the week. M = Morning, A = Afternoon, N = Night.</p>
          <RosterTab staff={staff} />
        </div>
      )}

      {tab === 'payroll' && <PayrollTab staff={staff} />}
      {tab === 'performance' && <PerformanceTab staff={staff} />}
    </PageShell>
  );
}

/* ── Payroll Tab (API-backed · CTC + attendance) ── */
function PayrollTab({ staff }) {
  const monthKey = new Date().toISOString().slice(0, 7);
  const monthLabel = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  const [lines, setLines] = useState([]);
  const [processed, setProcessed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ipApi.getHrPayroll(monthKey);
        if (data?.payroll?.lines?.length && !cancelled) {
          setLines(data.payroll.lines);
          setProcessed(true);
          return;
        }
        let attendance = [];
        try {
          const att = await ipApi.getHrAttendance({ month: monthKey });
          attendance = att?.records || [];
        } catch (_) { /* optional */ }
        if (!cancelled) {
          const { buildPayrollLine, toApiPayrollLines } = await import('../../utils/payrollCalc');
          const built = (staff || []).map((s) => {
            let absent = 0;
            const id = s.staffId || s.uid;
            for (const r of attendance) {
              if (String(r.staffId) !== String(id)) continue;
              const st = String(r.status || '').toLowerCase();
              if (st === 'absent' || st === 'leave' || st === 'on leave') absent += 1;
              else if (st.includes('half')) absent += 0.5;
            }
            return buildPayrollLine(s, absent);
          });
          setLines(toApiPayrollLines(built));
          setProcessed(false);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || 'Could not load payroll');
          setLines([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [staff, monthKey]);

  const processPayroll = async () => {
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      await ipApi.saveHrPayroll({ month: monthKey, lines });
      setProcessed(true);
      setMsg('Payroll processed and saved for this month.');
    } catch (e) {
      setErr(e.message || 'Process failed');
    } finally {
      setBusy(false);
    }
  };

  const totalPayroll = lines.reduce((s, e) => s + Number(e.net || 0), 0);

  return (
    <div>
      <div className="info-banner mb">Uses staff CTC + attendance absences for {monthLabel}. Open <strong>Payroll &amp; Accounts</strong> to edit CTC and print full slips.</div>
      {err && <div className="error mb">{err}</div>}
      <div className="closing-grand-total" style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span>Total Payroll — {monthLabel}{processed ? ' (saved)' : ''}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontFamily: 'Outfit, sans-serif', fontSize: 24, fontWeight: 900, color: '#065f46' }}>₹{totalPayroll.toLocaleString('en-IN')}</span>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={processPayroll}>{busy ? 'Saving…' : processed ? 'Re-process' : 'Process payroll'}</button>
        </div>
      </div>
      {msg && <div className="info-banner mb">{msg}</div>}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Salary Breakdown — {monthLabel}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Employee', 'Role', 'Basic', 'HRA', 'DA', 'Absent', 'PF', 'TDS', 'Net Pay'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((e) => (
                <tr key={e.staffId || e.name}>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontWeight: 700 }}>{e.name}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#64748b' }}>{e.role}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9' }}>₹{Number(e.base || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#0d6e6a' }}>₹{Number(e.hra || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#0d6e6a' }}>₹{Number(e.da || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: Number(e.absent) > 0 ? '#dc2626' : '#64748b' }}>{e.absent || 0}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#ef4444' }}>-₹{Number(e.pf || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', color: '#ef4444' }}>{e.tds ? `-₹${Number(e.tds).toLocaleString('en-IN')}` : '—'}</td>
                  <td style={{ padding: '8px 10px', border: '1px solid #f1f5f9', fontWeight: 800, color: '#10b981' }}>₹{Number(e.net || 0).toLocaleString('en-IN')}</td>
                </tr>
              ))}
              {lines.length === 0 && <tr><td colSpan={9} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No staff found</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="hint" style={{ fontSize: 11, marginTop: 8 }}>* Set CTC per staff in Staff &amp; Logins or Payroll &amp; Accounts. Absent days come from this month&apos;s attendance.</p>
      </div>
    </div>
  );
}

/* ── Performance Tab ── */
const PERF_RATINGS = ['Excellent', 'Good', 'Satisfactory', 'Needs Improvement', 'Unsatisfactory'];
const PERF_COLOR = { Excellent: '#10b981', Good: '#3b82f6', Satisfactory: '#f59e0b', 'Needs Improvement': '#f97316', Unsatisfactory: '#ef4444' };

function PerformanceTab({ staff }) {
  const monthKey = new Date().toISOString().slice(0, 7);
  const [attToday, setAttToday] = useState({});
  const [reviews, setReviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    ipApi.getHrAttendance(TODAY).then((d) => {
      const map = {};
      (d.records || []).forEach((r) => { map[r.staffId] = r.status; });
      if (!cancelled) setAttToday(map);
    }).catch(() => {
      if (!cancelled) setMsg('Could not load today\'s attendance.');
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ipApi.getHrPerformance(monthKey);
        const saved = new Map((data.reviews || []).map((r) => [r.staffId, r]));
        const merged = (staff || []).map((s) => {
          const staffId = s.staffId || s.uid || s.name;
          const prev = saved.get(staffId);
          return {
            staffId,
            staffName: s.name,
            role: s.role,
            rating: prev?.rating || 'Good',
            remarks: prev?.remarks || '',
            todayStatus: attToday[staffId] || attToday[s.staffId] || 'Not marked',
          };
        });
        if (!cancelled) setReviews(merged);
      } catch {
        if (!cancelled) {
          setMsg('Could not load performance reviews.');
          setReviews((staff || []).map((s) => ({
            staffId: s.staffId || s.uid || s.name,
            staffName: s.name,
            role: s.role,
            rating: '',
            remarks: '',
            todayStatus: attToday[s.staffId || s.uid] || 'Not marked',
          })));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [staff, attToday, monthKey]);

  const updateRating = (staffId, rating) => setReviews((r) => r.map((x) => (x.staffId === staffId ? { ...x, rating } : x)));

  const saveReviews = async () => {
    setBusy(true);
    setMsg('');
    try {
      await ipApi.saveHrPerformance({ month: monthKey, reviews });
      setMsg('Performance reviews saved.');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Performance Reviews — {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</h3>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={saveReviews}>{busy ? 'Saving…' : 'Save reviews'}</button>
      </div>
      {msg && <div className="info-banner mb">{msg}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {reviews.map((rev) => (
          <div key={rev.staffId} className="card" style={{ borderLeft: `4px solid ${PERF_COLOR[rev.rating] || '#e2e8f0'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{rev.staffName}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{rev.role} · Today: <strong style={{ color: rev.todayStatus === 'Present' ? '#16a34a' : rev.todayStatus === 'Absent' ? '#dc2626' : '#64748b' }}>{rev.todayStatus}</strong></div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>HR rating (saved to server)</div>
                <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 16, fontWeight: 900, color: PERF_COLOR[rev.rating] || '#3b82f6' }}>{rev.rating}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {PERF_RATINGS.map((r) => (
                <button key={r} type="button" onClick={() => updateRating(rev.staffId, r)}
                  style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, border: `1.5px solid ${PERF_COLOR[r]}40`, background: rev.rating === r ? PERF_COLOR[r] : `${PERF_COLOR[r]}15`, color: rev.rating === r ? '#fff' : PERF_COLOR[r], cursor: 'pointer' }}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        ))}
        {reviews.length === 0 && <p className="hint table-empty">No staff to review. Add staff first.</p>}
      </div>
    </div>
  );
}
